import { useFormContext } from "react-hook-form";
import { Lock, ShieldAlert } from "lucide-react";
import { LGoButton } from "@/ui/branded";
import { FormControl, FormField, FormItem, FormMessage } from "@/ui/primitives/form";
import {
  unreadableCustomFields,
  type StoredCustomFields,
} from "@/domain/custom-fields/types";
import { CustomFieldsEditor } from "@/features/custom-fields/components/CustomFieldsEditor";
import { definitionsToNewRows } from "@/features/custom-fields/form-schema";
import { offerCustomFieldsCopy } from "@/features/offers/offer-authoring-copy";
import type { OfferFormValues } from "@/features/offers/form-schema";

interface OfferCustomFieldsSectionProps {
  /** Undefined until an activity is picked: there is nothing to inherit from
   *  yet, which is not the same as an activity that asks nothing. */
  inheritedFields?: StoredCustomFields;
  isEditing: boolean;
}

const panelClassName = "space-y-3 rounded-lg border border-dashed border-border bg-muted/40 p-4";
const panelHeadingClassName = "flex items-center gap-1.5 text-sm font-medium text-foreground";

export function OfferCustomFieldsSection({
  inheritedFields,
  isEditing,
}: OfferCustomFieldsSectionProps) {
  const form = useFormContext<OfferFormValues>();
  const mode = form.watch("custom_fields_mode");

  const startOverriding = () => {
    form.setValue("custom_fields_mode", "override");
    form.setValue(
      "custom_fields",
      definitionsToNewRows(Array.isArray(inheritedFields) ? inheritedFields : null),
    );
  };

  const takeActivityFields = () => {
    form.setValue("custom_fields_mode", "inherit");
    form.setValue("custom_fields", []);
  };

  return (
    <>
      {mode === "override" ? (
        <OverridePanel onTakeActivityFields={takeActivityFields} />
      ) : mode === "unreadable" ? (
        <UnreadablePanel onTakeActivityFields={takeActivityFields} />
      ) : (
        <InheritPanel
          inheritedFields={inheritedFields}
          isEditing={isEditing}
          onStartOverriding={startOverriding}
        />
      )}
    </>
  );
}

function OverridePanel({ onTakeActivityFields }: { onTakeActivityFields: () => void }) {
  const form = useFormContext<OfferFormValues>();

  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground">{offerCustomFieldsCopy.overrideLead}</p>
      <FormField
        control={form.control}
        name="custom_fields"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <CustomFieldsEditor rows={field.value} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <LGoButton type="button" variant="ghost" size="sm" onClick={onTakeActivityFields}>
        {offerCustomFieldsCopy.takeTemplateFields}
      </LGoButton>
    </div>
  );
}

/** Saving from here omits the questions from the request, so the stored ones
 *  survive an edit of the price beside them. Taking the activity's is the only
 *  action offered because it is the only one that means something the portal can
 *  carry out: it sends a null the API stores, and the override is really gone. */
function UnreadablePanel({ onTakeActivityFields }: { onTakeActivityFields: () => void }) {
  return (
    <div className={panelClassName}>
      <p className={panelHeadingClassName}>
        <ShieldAlert className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        {offerCustomFieldsCopy.unreadableOwnHeading}
      </p>
      <p className="text-sm text-muted-foreground">{offerCustomFieldsCopy.unreadableOwnLead}</p>
      <LGoButton type="button" variant="secondary" size="sm" onClick={onTakeActivityFields}>
        {offerCustomFieldsCopy.takeTemplateFields}
      </LGoButton>
    </div>
  );
}

function InheritPanel({
  inheritedFields,
  isEditing,
  onStartOverriding,
}: {
  inheritedFields: StoredCustomFields | undefined;
  isEditing: boolean;
  onStartOverriding: () => void;
}) {
  if (inheritedFields === undefined) {
    return (
      <div className={panelClassName}>
        <p className="text-sm text-muted-foreground">
          {isEditing
            ? offerCustomFieldsCopy.templateFieldsUnavailable
            : offerCustomFieldsCopy.noTemplatePicked}
        </p>
      </div>
    );
  }

  if (inheritedFields === unreadableCustomFields) {
    return (
      <div className={panelClassName}>
        <p className={panelHeadingClassName}>
          <ShieldAlert className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          {offerCustomFieldsCopy.unreadableInheritedHeading}
        </p>
        <p className="text-sm text-muted-foreground">
          {offerCustomFieldsCopy.unreadableInheritedLead}
        </p>
        <LGoButton type="button" variant="secondary" size="sm" onClick={onStartOverriding}>
          {offerCustomFieldsCopy.askDifferently}
        </LGoButton>
      </div>
    );
  }

  const inherited = inheritedFields ?? [];

  return (
    <div className={panelClassName}>
      <p className={panelHeadingClassName}>
        <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        {offerCustomFieldsCopy.inheritedHeading}
      </p>
      {inherited.length === 0 ? (
        <p className="text-sm text-muted-foreground">{offerCustomFieldsCopy.inheritedNothing}</p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{offerCustomFieldsCopy.inheritedLead}</p>
          <ul className="space-y-1">
            {inherited.map((field) => (
              <li key={field.key} className="text-sm font-medium text-foreground">
                {field.label}
                {field.required && (
                  <span className="font-normal text-muted-foreground">
                    {` (${offerCustomFieldsCopy.requiredMark})`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      <LGoButton type="button" variant="secondary" size="sm" onClick={onStartOverriding}>
        {offerCustomFieldsCopy.askDifferently}
      </LGoButton>
    </div>
  );
}
